import { useCallback, useMemo } from 'react';
import { keepPreviousData, useInfiniteQuery } from '@zg-rentals/react-query5';
import type { InfiniteData } from '@zg-rentals/react-query5';
import {
  SatelliteWebRenterConversationHandler_getLatestConversations,
  SatelliteWebRenterConversationHandler_getLatestConversations_Path,
} from '@zg-rentals/ts-satellite-client';
import type {
  SatelliteWebRenterConversationGetLatestConversationsDto,
  SatelliteWebRenterConversationGetLatestConversationsRequestDto,
  SatelliteWebRenterConversationGetLatestConversationsResponseDto,
} from '@zg-rentals/ts-satellite-client';
import { logError } from '@zg-rentals/log-error';
import type { SatelliteConversationFolderTypeV2 } from '@zg-rentals/ts-satellite-client';
import arrayUtils from '../utils/arrayUtils';

type UseInfiniteConversationListOptions = {
  folder: SatelliteConversationFolderTypeV2;
  daysBack: number | null;
  isWebSocketEnabledAndReady: boolean;
  pollIntervalMs: number;
};

type UseInfiniteConversationListResult = {
  conversations: Array<SatelliteWebRenterConversationGetLatestConversationsDto>;
  hasNextPage: boolean;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  isRefetching: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  fetchNextPage: () => Promise<void>;
};

/**
 * Custom hook for managing conversation list data fetching with React Query's useInfiniteQuery.
 * Handles pagination, WebSocket integration, and polling fallback.
 */
export const useInfiniteConversationList = ({
  folder,
  daysBack,
  isWebSocketEnabledAndReady,
  pollIntervalMs,
}: UseInfiniteConversationListOptions): UseInfiniteConversationListResult => {
  const basePostData: Omit<SatelliteWebRenterConversationGetLatestConversationsRequestDto, 'beforeConversationId'> =
    useMemo(
      () => ({
        folder,
        daysBack: daysBack ?? undefined,
      }),
      [folder, daysBack],
    );

  // Query key structure: [API_PATH, { folder, daysBack }]
  // IMPORTANT: When invalidating this cache from other components (e.g., MessageList),
  // use the exact same structure to ensure cache invalidation works correctly
  const queryKey = useMemo(
    () => [SatelliteWebRenterConversationHandler_getLatestConversations_Path, basePostData],
    [basePostData],
  );

  const {
    data,
    error,
    refetch: infiniteRefetch,
    fetchNextPage: infiniteFetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    isFetching,
  } = useInfiniteQuery<
    SatelliteWebRenterConversationGetLatestConversationsResponseDto,
    Error,
    InfiniteData<SatelliteWebRenterConversationGetLatestConversationsResponseDto>,
    Array<string | Omit<SatelliteWebRenterConversationGetLatestConversationsRequestDto, 'beforeConversationId'>>,
    string | undefined
  >({
    queryKey,
    queryFn: async ({ pageParam }) => {
      try {
        const postData: SatelliteWebRenterConversationGetLatestConversationsRequestDto = {
          ...basePostData,
          beforeConversationId: pageParam,
        };
        return await SatelliteWebRenterConversationHandler_getLatestConversations(postData);
      } catch (err) {
        logError({ error: err, context: 'Error fetching conversations in useInfiniteConversationList' });
        throw err;
      }
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => {
      if (lastPage?.hasNextPage !== true || !lastPage?.conversations?.length) return undefined;
      const last = lastPage.conversations[lastPage.conversations.length - 1];
      return last?.conversationId;
    },
    // Keep previous data visible while refetching (prevents skeleton flash)
    placeholderData: keepPreviousData,
    // Use polling when WebSocket is not available
    refetchInterval: isWebSocketEnabledAndReady ? undefined : pollIntervalMs,
    refetchIntervalInBackground: false,
    // Disable automatic refetching on window focus/reconnect - we handle this via WebSocket
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // Avoid duplicate fetch when ConversationList double-mounts (e.g. React Strict Mode): treat data as fresh briefly and don't refetch on mount when cache exists
    refetchOnMount: false,
    staleTime: 2000,
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  // Folder change is handled by the inbox page: it invalidates the new folder's query in handleFolderChange,
  // so when this hook observes that query we get one refetch. No effect here avoids duplicate getLatestConversations.

  // Flatten all pages and deduplicate conversations (guard against undefined page.conversations, e.g. empty folder API response)
  const conversations = useMemo(() => {
    if (!data?.pages) return [];
    const allConversations = data.pages.flatMap((page) => page?.conversations ?? []);
    return arrayUtils.removeDuplicates(allConversations, 'conversationId');
  }, [data?.pages]);

  // Coalesce: skip if a refetch is already in progress to avoid duplicate getLatestConversations
  // (e.g. MessageList refetchQueries + ConversationList WebSocket refetch on new message).
  const refetch = useCallback(async () => {
    if (isFetching) return;
    try {
      await infiniteRefetch();
    } catch (err) {
      logError({ error: err, context: 'Error refetching conversations' });
      throw err;
    }
  }, [infiniteRefetch, isFetching]);

  const fetchNextPage = useCallback(async () => {
    try {
      await infiniteFetchNextPage();
    } catch (err) {
      logError({ error: err, context: 'Error fetching next page of conversations' });
    }
  }, [infiniteFetchNextPage]);

  return {
    conversations,
    hasNextPage: hasNextPage ?? false,
    isLoading,
    isFetchingNextPage,
    isRefetching: isFetching && !isFetchingNextPage && !isLoading,
    error: error as Error | null,
    refetch,
    fetchNextPage,
  };
};
